/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { Drawer, Form, message } from 'antd';
import moment from 'moment';

import Utils from '../../../Assets/Scripts/Utils';
import ModalUploadFile from '../../../Components/Schedule/ModalUploadFile';
import TaskCommentFunctions from '../../../Components/Schedule/TaskCommentFunctions';
import useSignalR from '../../../Hooks/useSignalR';
import { api } from '../../../Services/axiosService';
import { fetchComments, fetchTasks } from '../API/ScheduleSchedulingAPI';
import SchedulingFunctions from '../API/SchedulingFunctions';
import FormLoader from '../Assets/FormLoader';

import ConfirmationModal from './ConfirmationModal';
import TaskDrawerExtra from './TaskDrawerExtra';
import TaskDrawerFooter from './TaskDrawerFooter';
import TaskDrawerOperatorFooter from './TaskDrawerOperatorFooter';
import TaskForm from './TaskForm';

import './TaskDrawer.scss';

function resolveResourceIdForSchedulingSave(taskToSave, columns, gridAppointments) {
  const persistedResourceId = taskToSave.resourceId;
  const storedType = taskToSave.resource || taskToSave.trainingType;

  if (Array.isArray(columns) && columns.length > 0) {
    const placement = SchedulingFunctions.resolveAgendaResourceId(
      persistedResourceId,
      storedType,
      columns
    );

    if (placement.kind === 'remapped' && placement.resourceId != null) {
      return placement.resourceId;
    }

    return persistedResourceId;
  }

  const gridAppointment = gridAppointments?.find((appointment) => appointment.id === taskToSave.id);
  if (gridAppointment?.resourceId == null) {
    return persistedResourceId;
  }

  return gridAppointment.resourceId;
}

function TaskDrawer({
  taskId,
  setIsTaskDrawerVisible,
  fieldsPermissions,
  stages,
  trainingsOptions,
  ctResources,
  instructorOptions,
  transportOptions,
  vehicleOptions,
  driverOptions,
  reasonNoBillingOptions,
  invoiceIssuersOptions,
  productOptions,
  certificateModelOptions,
  quoteList,
  appointments,
  taskAcceptanceDeadline,
  ctResourcesSlots,
  setAppointments,
  setQuoteList,
  isTaskReadOnly,
  setIsTaskReadOnly,
  isLogistic,
  page,
  scheduleType,
  confirmTask,
  denyTask,
  setShowConclusionModal,
  setShowModalIntercurrence,
  setCurrentTask,
  initialTab,
  settings,
  resourceId,
  resourceName,
  fieldLayout,
  roleId,
}) {
  const [taskData, setTaskData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [uploadFileType, setUploadFileType] = useState(null);
  const [isUploadModalOpen, setUploadFileModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationModalConfirmAction, setConfirmationModalConfirmAction] = useState('confirm');
  const [isEditing, setIsEditing] = useState(false);
  const [showOperatorFooter, setShowOperatorFooter] = useState(true);
  const [oldTaskData, setOldTaskData] = useState(null);

  const [form] = Form.useForm();
  const hubConnection = useSignalR('tasks');

  useEffect(() => {
    if (taskData) {
      form.setFieldsValue({
        ...taskData,
        customFields: taskData.customFields ?? taskData.CustomFields ?? {},
      });
    }
  }, [taskData, isEditing]);

  const notifySameInstructors = () => {
    message.warning('O "Instrutor" e o "Instrutor Auxiliar" não podem ser o mesmo!');
  };

  // Função para remover a marcação "Atualizado"
  const removeQuoteOrDealUpdatedBadge = (taskToUpdate) => {
    api
      .put(`/Task/RemoveQuoteOrDealUpdatedBadge?taskId=${taskToUpdate.id}`)
      .then(() => {})
      .catch((error) => {
        Utils.logError(error);
        message.error('Oops. Algo deu errado ao remover a marcação "Atualizado"!');
      });
  };

  // Função para buscar os dados da tarefa
  const fetchTaskData = async () => {
    try {
      // Busca a tarefa pelo ID
      const [newTask] = await fetchTasks({ TaskIdList: [taskId] });
      newTask.scheduleType = scheduleType;
      const openTask = JSON.parse(JSON.stringify(newTask)); // Deep Copy da task
      setOldTaskData(openTask);

      if (scheduleType) {
        setCurrentTask(openTask);
      }

      SchedulingFunctions.mapTimeFields(openTask);
      SchedulingFunctions.mapFormData(openTask);
      setTaskData(openTask);

      // Remove a marcação "Atualizado" se necessário
      if (newTask.quoteOrDealUpdated && !scheduleType) {
        removeQuoteOrDealUpdatedBadge(newTask);
      }

      const commentsCombined = await fetchComments(newTask);

      setComments(commentsCombined);

      // Busca disponibilidade dos instrutores
      const availableInstructorsData = await SchedulingFunctions.fetchInstructorsAvailability(
        instructorOptions,
        openTask.startDate,
        openTask.endDate
      );

      let combinedInstructorList = [];
      if (availableInstructorsData?.length > 0) {
        combinedInstructorList = instructorOptions?.map((instructor) => {
          const availability = availableInstructorsData.find((avail) => avail.id === instructor.id);
          const trainingGroupsList = instructor?.trainingKind?.map(({ id }) => id);

          return {
            label: instructor.name?.toUpperCase(),
            value: instructor.id,
            hasTrainings: availability?.hasTrainings ?? false,
            isBlocked: availability?.isBlocked ?? false,
            disabled: availability?.isBlocked ?? false,
            trainingGroupsList,
          };
        });
      }

      setAvailableInstructors(combinedInstructorList);
      return newTask;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao buscar dados da tarefa:', error);
      message.error('Oops. Algo deu errado ao tentar buscar os dados do Treinamento!');
      return null;
    }
  };

  // Função chamada quando o modal é fechado
  const handleClose = () => {
    // Remove a marcação "Atualizado" na tela
    if (taskData && taskData.quoteOrDealUpdated) {
      // Para usuários de logística, apenas atualiza appointments (não quoteList)
      const taskQuote = !isLogistic
        ? quoteList
            ?.find(({ id }) => id === taskData.dealId)
            ?.tasks?.find(({ id }) => id === taskData.id)
        : null;
      const taskAppointment = appointments?.find(({ id }) => id === taskData.id);
      if (taskQuote) {
        taskQuote.quoteOrDealUpdated = false;
      }
      if (taskAppointment) {
        taskAppointment.quoteOrDealUpdated = false;
      }
    }

    setIsTaskDrawerVisible(false);
  };

  // Função para salvar os dados do formulário
  const handleActionUpdate = async (type) => {
    try {
      setIsLoading(true);

      const values = await form.validateFields();

      const userData = JSON.parse(localStorage.getItem('conecta__userData'));
      let btnAction = null;
      let newStageId = null;
      let otherDataChange = null;

      switch (type) {
        case 'confirm':
          newStageId = 3; // 3 = Estágio "Confirmado"
          btnAction = 'Confirmar';
          otherDataChange = {
            confirmedBy: userData.name,
            idConfirmedBy: parseInt(userData.id, 10),
            confirmationDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
          };
          break;
        case 'deny':
          newStageId = 4; // 4 = Estágio "Negado"
          btnAction = 'Negar';
          break;
        case 'noClientContact':
          newStageId = 13; // 13 = Estágio "Reservado Sem Confirmação"
          btnAction = 'SemContatoCliente';
          break;
        default:
          newStageId = values?.stage?.id;
          break;
      }

      if (values?.stage?.id === 14) {
        newStageId = 14;
      }

      if (values.hasDocumentation) {
        newStageId = 5;
      }

      const newStage = stages.find(({ id }) => id === newStageId);

      let dataToSave = taskData;
      const oldTaskData = JSON.parse(JSON.stringify(dataToSave));

      // Atualiza o objeto antes de enviar
      dataToSave = { ...dataToSave, ...values, ...otherDataChange, stage: newStage };

      // Não faz validação de Slot para treinamentos de Instrutor e Motorista
      if (!scheduleType) {
        dataToSave.resourceId = resolveResourceIdForSchedulingSave(
          dataToSave,
          ctResources,
          appointments
        );

        const shouldCancel = SchedulingFunctions.formatAppointmmentInfo(
          dataToSave,
          ctResources,
          taskAcceptanceDeadline,
          ctResourcesSlots,
          trainingsOptions
        );

        if (shouldCancel) {
          return;
        }
      }

      if (
        dataToSave.mainInstructor &&
        dataToSave.assistantInstructor &&
        dataToSave.mainInstructor === dataToSave.assistantInstructor
      ) {
        notifySameInstructors();
        return;
      }

      const apiUrl = btnAction
        ? `/Task/UpdateTaskScheduling?btnAction=${btnAction}`
        : '/Task/UpdateTaskScheduling';

      // Enviar para atualizar
      const { data: updated } = await api.put(apiUrl, dataToSave);

      dataToSave.stage = updated.stage;

      setAppointments((prev) => {
        const mapped = prev.map((appt) =>
          appt.id === dataToSave.id ? { ...appt, ...dataToSave } : appt
        );

        const withPossiblyAdded = mapped.some((appt) => appt.id === dataToSave.id)
          ? mapped
          : [...mapped, { ...dataToSave }];

        return withPossiblyAdded.map((appt, index) => ({ ...appt, index }));
      });

      if (setQuoteList && !isLogistic) {
        setQuoteList((prevQuoteList) =>
          (prevQuoteList ?? []).map((quote) => {
            const tasks = quote.tasks ?? [];
            if (quote.id === dataToSave.dealId) {
              const taskIndex = tasks.findIndex((task) => task.id === dataToSave.id);

              if (taskIndex !== -1) {
                const newTasks = [...tasks];
                newTasks[taskIndex] = { ...newTasks[taskIndex], ...dataToSave };
                return { ...quote, tasks: newTasks };
              }
            }
            // Retorna o quote original se não for o quote alvo ou se a task não for encontrada
            return quote;
          })
        );
      }

      // Gera Atividade conforme mudança de valores dos campos
      await TaskCommentFunctions.addTaskCommentActivities(dataToSave, oldTaskData, 'Scheduling');
      await TaskCommentFunctions.addTaskCommentHistory(dataToSave, oldTaskData, 'Scheduling');
      await TaskCommentFunctions.addTaskCommentDriver(dataToSave, oldTaskData, driverOptions);

      // Enviar Notificação para os Operadores (Instrutor, Instrutor Auxiliar, Motorista)
      await SchedulingFunctions.sendNotificationToOperators(dataToSave, oldTaskData);

      setTaskData();
      setComments();

      await fetchTaskData();

      message.success('Atualização dos Treinamentos concluída!');
    } catch (error) {
      if (error?.errorFields?.length > 0) {
        const [firstField] = error.errorFields;
        form.scrollToField(firstField.name);

        message.warn(
          'Alguns campos estão com preenchimento incorreto, realize os ajustes necessários e tente novamente!',
          7
        );
      } else {
        Utils.logError(error);
        message.error('Oops. Algo deu errado ao tentar atualizar os Treinamentos selecionados!');
      }
    } finally {
      setIsEditing(false);
      setIsLoading(false);
    }
  };

  const handlefetchTaskData = async () => {
    setIsLoading(true);
    await fetchTaskData();
    setIsLoading(false);
  };

  const handleCreateDriver = () => {
    // Função desabilitada pois o Drawer de cadastro rápido foi removido do TaskDrawer
  };

  useEffect(() => {
    handlefetchTaskData();

    return () => {
      if (setIsTaskReadOnly) {
        setIsTaskReadOnly(false);
      }
    };
  }, []);

  useEffect(() => {
    if (hubConnection) {
      hubConnection.on('ReceiveTaskUpdate', (updatedTask) => {
        if (updatedTask.id === taskId) {
          updatedTask.scheduleType = scheduleType;
          const openTask = JSON.parse(JSON.stringify(updatedTask));

          SchedulingFunctions.mapTimeFields(openTask);
          SchedulingFunctions.mapFormData(openTask);

          setTaskData(openTask);
        }
      });
    }

    return () => {
      if (hubConnection) {
        hubConnection.off('ReceiveTaskUpdate');
      }
    };
  }, [hubConnection, taskId]);

  // Renderiza o footer do Drawer
  const renderFooter = () => {
    const commonProps = {
      fieldsPermissions,
      task: taskData,
      isLoading,
      handleActionUpdate,
      setIsEditing,
      setIsConfirmationModalOpen,
      setConfirmationModalConfirmAction,
      form,
      oldTaskData,
    };

    const operatorProps = {
      setShowConclusionModal,
      scheduleType,
      confirmTask,
      denyTask,
      setShowOperatorFooter,
    };

    if (taskData && !isTaskReadOnly && isEditing) {
      return <TaskDrawerFooter {...commonProps} />;
    }

    if (scheduleType) {
      return <TaskDrawerOperatorFooter {...commonProps} {...operatorProps} />;
    }

    return null;
  };

  const renderExtra = () => {
    if (taskData) {
      return (
        <TaskDrawerExtra
          isLoading={isLoading}
          fieldsPermissions={fieldsPermissions}
          form={form}
          task={taskData}
          isTaskReadOnly={isTaskReadOnly}
          setIsEditing={setIsEditing}
          certificateModelOptions={certificateModelOptions}
          productOptions={productOptions}
          handleTaskUpdate={handleActionUpdate}
          setComments={setComments}
          page={page}
          scheduleType={scheduleType}
          setShowModalIntercurrence={setShowModalIntercurrence}
          fieldLayout={fieldLayout}
          roleId={roleId}
        />
      );
    }

    return null;
  };

  return (
    <Drawer
      title={Utils.formatAgendaCardTitle(taskData) || 'Detalhes da Tarefa'}
      placement="right"
      width="90vw"
      className="training-card-drawer"
      onClose={handleClose}
      maskClosable={false}
      closable={!isLoading}
      open={true}
      footer={renderFooter()}
      footerStyle={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: !showOperatorFooter && !isEditing ? 0 : '10px 16px',
      }}
      extra={renderExtra()}
    >
      {(!taskData || !comments || isLoading) && <FormLoader />}

      {taskData && comments && !isLoading && (
        <TaskForm
          form={form}
          task={taskData}
          isLoading={isLoading}
          fieldsPermissions={fieldsPermissions}
          stages={stages}
          trainingsOptions={trainingsOptions}
          transportOptions={transportOptions}
          reasonNoBillingOptions={reasonNoBillingOptions}
          invoiceIssuersOptions={invoiceIssuersOptions}
          vehicleOptions={vehicleOptions}
          driverOptions={driverOptions}
          comments={comments}
          availableInstructors={availableInstructors}
          instructorOptions={instructorOptions}
          handleTaskUpdate={handleActionUpdate}
          setUploadFileType={setUploadFileType}
          setUploadFileModalOpen={setUploadFileModalOpen}
          setComments={setComments}
          isTaskReadOnly={isTaskReadOnly}
          isEditing={isEditing}
          scheduleType={scheduleType}
          initialTab={initialTab}
          settings={settings}
          onCreateDriver={handleCreateDriver}
          resourceId={resourceId}
          resourceName={resourceName}
          fieldLayout={fieldLayout}
          roleId={roleId}
        />
      )}

      {isUploadModalOpen && (
        <ModalUploadFile
          setIsOpen={setUploadFileModalOpen}
          loading={isLoading}
          currentOpenTask={taskData}
          handleTaskUpdate={handleActionUpdate}
          type={uploadFileType}
          formTaskDrawer={form}
        />
      )}

      {isConfirmationModalOpen && (
        <ConfirmationModal
          setIsOpen={setIsConfirmationModalOpen}
          onConfirm={() => {
            handleActionUpdate(confirmationModalConfirmAction);
          }}
          formData={{ ...taskData, ...form.getFieldsValue() }}
          form={form}
        />
      )}
    </Drawer>
  );
}

export default TaskDrawer;
